import AiAssistantButton from "@/components/ai-assistant-button"
import HelpModal from "@/components/modal-help"
import QuestionTipsWidget from "@/widgets/question-tips.widget"
import TasksGenerateModal from "@/modals/tasks-generate.modal"
import { useAppSelector } from "@/core/root-reducer"
import { Question } from "@/redux/question.types"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes, faCircleInfo, faMagnifyingGlassChart } from "@fortawesome/free-solid-svg-icons"
import Modal from "@/components/modal"
import Button from "@/components/button"
import { AccountOptions } from "@/redux/account.types"
import type { BenchmarkEntry } from "../model/benchmarkQuestion.context"
import type { BenchmarkNavItem } from "./DashboardBenchmarkDetail"
import DashboardBenchmarkDetailWidget from "./DashboardBenchmarkDetail"
import type { BenchmarkDistribution } from "../model/benchmarkQuestion.types"
import BenchmarkDashboardInfoModal from "./modals/BenchmarkDashboardInfoModal"

interface Props {
  accountName: string
  accountOptions: AccountOptions
  score: number
  axisColor: string
  questionAid?: string | null
  benchmarkEntry?: BenchmarkEntry | null
  sector: string | null
  navItems: BenchmarkNavItem[]
  navCurrentIndex: number
  onNavigate: (index: number) => void
  onClose: () => void
  domain?: "question" | "nps"
  distributionOverride?: BenchmarkDistribution | null
}

export default function BenchmarkDetailModal({
  accountName,
  accountOptions,
  score,
  axisColor,
  questionAid,
  benchmarkEntry,
  sector,
  navItems,
  navCurrentIndex,
  onNavigate,
  onClose,
  domain,
  distributionOverride
}: Props) {
  const { t } = useTranslation()
  const [showInfo, setShowInfo] = useState(false)
  const [showAssistant, setShowAssistant] = useState(false)
  const session = useAppSelector((state) => state._session)
  const topics = useAppSelector((state) => state.topic.list)
  const tasks = useAppSelector((state) => state.actionPlan.active.tasks)
  const currentItem = navItems[navCurrentIndex]
  const topic = topics.find((item) => item.Questions.some((question) =>
    currentItem?.liveQuestionId ? question.id === currentItem.liveQuestionId : question.label === currentItem?.label
  ))
  const question = topic?.Questions.find((item) =>
    currentItem?.liveQuestionId ? item.id === currentItem.liveQuestionId : item.label === currentItem?.label
  )
  const canUseAssistant = session.getModuleObserver("assistant") && question?.type === "scale" &&
    !question.primaryId && !question.options.multipleItems
  const isTasked = tasks.some((task) => task.topicAid === topic?.aid)

  const sortedNavItems = useMemo(() => [...navItems].sort((a, b) => (a.delta ?? 0) - (b.delta ?? 0)), [navItems])
  const currentAid = navItems[navCurrentIndex]?.questionAid
  const sortedCurrentIndex = sortedNavItems.findIndex((i) => i.questionAid === currentAid)

  function navigateSorted(sortedIdx: number) {
    const aid = sortedNavItems[sortedIdx]?.questionAid
    const originalIdx = navItems.findIndex((i) => i.questionAid === aid)
    if (originalIdx >= 0) onNavigate(originalIdx)
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") navigateSorted(sortedCurrentIndex - 1)
      else if (e.key === "ArrowRight") navigateSorted(sortedCurrentIndex + 1)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [sortedCurrentIndex, navigateSorted]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Modal
        className="px-0 overflow-hidden w-[960px] max-w-[95vw]"
        // Reserves the scrollbar's width permanently so flipping between the histogram and the
        // question-list panel (slightly different heights) can't pop the scrollbar in/out and
        // shift the whole modal body sideways.
        contentClassName="[scrollbar-gutter:stable]"
        onClose={onClose}
        title={
          <div className="px-[40px] mb-0">
            <FontAwesomeIcon icon={faMagnifyingGlassChart} style={{ color: axisColor }} />
            &nbsp;{t(domain === "nps" ? "benchmark_nps_modal_title" : "benchmark_q_modal_title")}
          </div>
        }
        footer={
          <div className="flex items-center justify-end gap-2 w-full px-[40px]">
            {canUseAssistant && (
              <div className="mr-auto" data-tip={isTasked ? t("tasks_topic_used") : undefined}>
                <AiAssistantButton isAnimated={!isTasked} isChecked={isTasked} onClick={() => setShowAssistant(true)} />
              </div>
            )}
            <Button icon={faCircleInfo} onClick={() => setShowInfo(true)} isFullWidth>
              {t("benchmark_q_how_it_works")}
            </Button>
            <Button icon={faTimes} onClick={onClose}>
              {t("utils_close")}
            </Button>
          </div>
        }>
        <DashboardBenchmarkDetailWidget
          accountName={accountName}
          accountOptions={accountOptions}
          score={score}
          sector={sector}
          questionAid={questionAid}
          benchmarkEntry={benchmarkEntry}
          navItems={sortedNavItems}
          navCurrentIndex={sortedCurrentIndex}
          onNavigate={navigateSorted}
          domain={domain}
          distributionOverride={distributionOverride}
        />
      </Modal>

      {showAssistant && question && topic && (
        session.modules.survey.task ? (
          <TasksGenerateModal selectedTopic={topic} onClose={() => setShowAssistant(false)} />
        ) : (
          <HelpModal title={`${t("module_assistant")} — ${question.label}`} onClose={() => setShowAssistant(false)} isLeft>
            <QuestionTipsWidget question={new Question(question)} />
          </HelpModal>
        )
      )}
      {showInfo && <BenchmarkDashboardInfoModal onClose={() => setShowInfo(false)} domain={domain} />}
    </>
  )
}
